import React, { useEffect, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../../config";
import {
  BsEyeFill,
  BsPeopleFill,
  BsCalendarDateFill,
  BsPersonCheckFill,
  BsExclamationTriangleFill,
  BsPhoneFill,
  BsLaptopFill,
  BsRobot,
} from "react-icons/bs";

export default function OpsAnalytics() {
  const token = localStorage.getItem("userToken");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [days, setDays] = useState(30);

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_BASE_URL}/ops/analytics?days=${days}`, { headers });
        setData(res.data);
        setError("");
      } catch (err) {
        console.error("OpsAnalytics error", err.response?.data || err.message);
        setError("Unable to load analytics.");
      } finally {
        setLoading(false);
      }
    };
    fetchAnalytics();
  }, [days]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="ops-loading">
        <div className="ops-spinner" />
        <p>Loading analytics...</p>
      </div>
    );
  }

  if (error) {
    return <div className="ops-error"><BsExclamationTriangleFill /> {error}</div>;
  }

  const maxViews = Math.max(...(data.dailyViews || []).map((d) => d.views), 1);
  const deviceTotal = (data.deviceBreakdown?.mobile || 0) + (data.deviceBreakdown?.desktop || 0) + (data.deviceBreakdown?.bots || 0);

  const pct = (val) => deviceTotal ? Math.round((val / deviceTotal) * 100) : 0;

  return (
    <div>
      {/* Period selector */}
      <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontWeight: 600, fontSize: "0.85rem", color: "var(--pf-text-muted)" }}>Period:</span>
        {[7, 14, 30, 90].map((d) => (
          <button
            key={d}
            className={`btn btn-sm ${d === days ? "btn-primary" : "btn-outline-secondary"}`}
            onClick={() => setDays(d)}
          >
            {d}d
          </button>
        ))}
      </div>

      {/* Summary Cards */}
      <div className="ops-overview-grid">
        <div className="ops-overview-card">
          <div className="ops-overview-icon ops-icon-blue">
            <BsEyeFill />
          </div>
          <div className="ops-overview-info">
            <span className="ops-overview-label">Total Views</span>
            <strong className="ops-overview-value">{(data.totalViews || 0).toLocaleString()}</strong>
          </div>
        </div>
        <div className="ops-overview-card">
          <div className="ops-overview-icon ops-icon-green">
            <BsPeopleFill />
          </div>
          <div className="ops-overview-info">
            <span className="ops-overview-label">Unique Visitors</span>
            <strong className="ops-overview-value">{(data.uniqueVisitors || 0).toLocaleString()}</strong>
          </div>
        </div>
        <div className="ops-overview-card">
          <div className="ops-overview-icon ops-icon-orange">
            <BsCalendarDateFill />
          </div>
          <div className="ops-overview-info">
            <span className="ops-overview-label">Views Today</span>
            <strong className="ops-overview-value">{(data.viewsToday || 0).toLocaleString()}</strong>
          </div>
        </div>
        <div className="ops-overview-card">
          <div className="ops-overview-icon ops-icon-purple">
            <BsPersonCheckFill />
          </div>
          <div className="ops-overview-info">
            <span className="ops-overview-label">Visitors Today</span>
            <strong className="ops-overview-value">{(data.visitorsToday || 0).toLocaleString()}</strong>
          </div>
        </div>
      </div>

      {/* Daily Views Chart */}
      <div className="ops-section">
        <div className="ops-section-header">
          <h5 style={{ margin: 0, fontWeight: 700 }}>Daily Views</h5>
        </div>
        {data.dailyViews && data.dailyViews.length > 0 ? (
          <div style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 180, padding: "8px 0" }}>
            {data.dailyViews.map((d, i) => {
              const barH = Math.max((d.views / maxViews) * 160, 2);
              const dateStr = new Date(d.date).toLocaleDateString(undefined, { month: "short", day: "numeric" });
              return (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    minWidth: 0,
                  }}
                  title={`${dateStr}: ${d.views} views, ${d.visitors} visitors`}
                >
                  <span
                    style={{
                      fontSize: "0.65rem",
                      color: "var(--pf-text-muted)",
                      marginBottom: 2,
                      visibility: d.views > 0 ? "visible" : "hidden",
                    }}
                  >
                    {d.views}
                  </span>
                  <div
                    style={{
                      width: "100%",
                      maxWidth: 32,
                      height: barH,
                      background: "var(--pf-primary)",
                      borderRadius: "4px 4px 0 0",
                      opacity: 0.85,
                      transition: "height 0.3s ease",
                    }}
                  />
                  {/* Show date label every ~5 bars to avoid clutter */}
                  <span
                    style={{
                      fontSize: "0.6rem",
                      color: "var(--pf-text-muted)",
                      marginTop: 4,
                      visibility:
                        data.dailyViews.length <= 14 || i % Math.ceil(data.dailyViews.length / 10) === 0
                          ? "visible"
                          : "hidden",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {dateStr}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="text-muted text-center" style={{ padding: 32 }}>No data for this period.</p>
        )}
      </div>

      {/* Two-column layout for tables */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {/* Top Pages */}
        <div className="ops-section">
          <div className="ops-section-header">
            <h5 style={{ margin: 0, fontWeight: 700 }}>Top Pages</h5>
          </div>
          <div className="ops-table-wrapper">
            <table className="ops-table">
              <thead>
                <tr>
                  <th>Page</th>
                  <th style={{ textAlign: "right" }}>Views</th>
                </tr>
              </thead>
              <tbody>
                {(data.topPages || []).map((p, i) => (
                  <tr key={i}>
                    <td style={{ fontFamily: "monospace", fontSize: "0.85rem" }}>{p.path}</td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>{p.views.toLocaleString()}</td>
                  </tr>
                ))}
                {(!data.topPages || data.topPages.length === 0) && (
                  <tr><td colSpan={2} className="text-muted text-center">No data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Referrers */}
        <div className="ops-section">
          <div className="ops-section-header">
            <h5 style={{ margin: 0, fontWeight: 700 }}>Top Referrers</h5>
          </div>
          <div className="ops-table-wrapper">
            <table className="ops-table">
              <thead>
                <tr>
                  <th>Referrer</th>
                  <th style={{ textAlign: "right" }}>Views</th>
                </tr>
              </thead>
              <tbody>
                {(data.topReferrers || []).map((r, i) => (
                  <tr key={i}>
                    <td style={{ fontSize: "0.85rem", wordBreak: "break-all" }}>{r.referrer}</td>
                    <td style={{ textAlign: "right", fontWeight: 600 }}>{r.views.toLocaleString()}</td>
                  </tr>
                ))}
                {(!data.topReferrers || data.topReferrers.length === 0) && (
                  <tr><td colSpan={2} className="text-muted text-center">No data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Device Breakdown */}
      <div className="ops-section">
        <div className="ops-section-header">
          <h5 style={{ margin: 0, fontWeight: 700 }}>Device Breakdown</h5>
        </div>
        <div className="ops-overview-grid" style={{ marginBottom: 0 }}>
          <div className="ops-overview-card">
            <div className="ops-overview-icon ops-icon-blue">
              <BsLaptopFill />
            </div>
            <div className="ops-overview-info">
              <span className="ops-overview-label">Desktop</span>
              <strong className="ops-overview-value">
                {(data.deviceBreakdown?.desktop || 0).toLocaleString()}
              </strong>
              <span style={{ fontSize: "0.78rem", color: "var(--pf-text-muted)" }}>{pct(data.deviceBreakdown?.desktop || 0)}%</span>
            </div>
          </div>
          <div className="ops-overview-card">
            <div className="ops-overview-icon ops-icon-green">
              <BsPhoneFill />
            </div>
            <div className="ops-overview-info">
              <span className="ops-overview-label">Mobile</span>
              <strong className="ops-overview-value">
                {(data.deviceBreakdown?.mobile || 0).toLocaleString()}
              </strong>
              <span style={{ fontSize: "0.78rem", color: "var(--pf-text-muted)" }}>{pct(data.deviceBreakdown?.mobile || 0)}%</span>
            </div>
          </div>
          <div className="ops-overview-card">
            <div className="ops-overview-icon ops-icon-orange">
              <BsRobot />
            </div>
            <div className="ops-overview-info">
              <span className="ops-overview-label">Bots</span>
              <strong className="ops-overview-value">
                {(data.deviceBreakdown?.bots || 0).toLocaleString()}
              </strong>
              <span style={{ fontSize: "0.78rem", color: "var(--pf-text-muted)" }}>{pct(data.deviceBreakdown?.bots || 0)}%</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
